import { motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { FaAddressCard, FaPhoneAlt } from "react-icons/fa";
import { MdEmail, MdWatchLater } from "react-icons/md";
import { mediaSource } from "../utilities/mediautilities";
import { useTranslation } from "react-i18next"
import { I18N_KEYS } from "../languages/PlaceHolder.js";
import { fetchApiReferenceForm, fetchFormDefination, fetchReferenceForm, fileUpload, submitFormEntry } from "../services/formServices.js";
import { FORMNAMES, setBrowserTitle } from "../utilities/codes.js";
import toast from "react-hot-toast";
import Breadcrumb from "../components/Breadcrumb.jsx";
import { FormRenderer } from "@aatulwork/customform-renderer";
import { APIENDPOINT } from "../services/apiHelper.js";

const Contact = () => {
    const { schoolDetails } = useContext(schoolContext);
    const [formDefination, setFormDefination] = useState()
    const { t } = useTranslation()

    useEffect(() => {
        setBrowserTitle("Contact Us")
        getConatctUsFormDefination()
    }, [])

    const getConatctUsFormDefination = async () => {
        try {
            const data = await fetchFormDefination(FORMNAMES.CONTACTUS);
            setFormDefination(data)
        } catch (error) {
            toast.error(error?.message)
        }
    }

    const services = {
        formReference: {
            fetchOptions: async (formName, fieldName) => {
                try {
                    const response = await fetchReferenceForm(formName);
                    return response.map((entry) => ({
                        label: entry.payload?.[fieldName] || entry[fieldName] || `Entry ${entry._id}`,
                        value: entry._id,
                    }));
                } catch (error) {
                    console.error(`Error fetching ${formName} options:`, error);
                    return [];
                }
            },
        },
        apiReference: {
            fetchOptions: async (endpoint, labelField, valueField = '_id') => {
                const response = await fetchApiReferenceForm(endpoint);

                const items = Array.isArray(response) ? response : response?.data || [];

                return items.map((item) => ({
                    label: item[labelField],
                    value: item[valueField],
                }));
            },
        },
        fileUpload: {
            uploadFiles: async (formName, fieldName, files) => {
                const formData = new FormData();
                formData.append('formName', formName)
                formData.append('fieldName', fieldName)
                files.forEach(file => formData.append('files', file));

                const response = await fileUpload(formData);

                return response;
            },
        },
        fileBaseUrl: `${APIENDPOINT}/`,
        ckEditorLicenseKey: 'GPL',
    }

    const handleSubmit = async (data) => {
        try {
            const res = await submitFormEntry(FORMNAMES.CONTACTUS, data);
            if (!res?.success) return toast.error("We couldn’t send your message right now. Please try again in a moment.");
        } catch (error) {
            toast.error(error?.message);
        }
    };

    const getPrimaryColor = () => {
        return getComputedStyle(document.documentElement)
            .getPropertyValue("--color-primary")
            .trim();
    };

    const getSecondaryColor = () => {
        return getComputedStyle(document.documentElement)
            .getPropertyValue("--color-secondary")
            .trim();
    };

    return (
        <>
            <div className="w-full">
                {/* Hero Section */}
                <section className="relative bg-cover bg-center py-36 px-6">
                    <img
                        src={mediaSource(schoolDetails?.contact_us?.fileUrl)}
                        alt={"Page banner"}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                </section>
                <Breadcrumb />
                {/* Contact Section */}
                <section className="py-20 px-6 bg-gray-50">
                    <div className=" common-container grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Contact Info */}
                        {(schoolDetails?.address || schoolDetails?.contact_number || schoolDetails?.email || schoolDetails?.working_hours) &&
                            <motion.div
                                className="bg-accent rounded-3xl shadow-2xl p-10 flex flex-col justify-start gap-4"
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="text-3xl font-bold text-primary mb-8">
                                    {t(I18N_KEYS.CONTACTUSTITLE1)}
                                </h2>
                                <ul className="space-y-6 text-primary">
                                    {schoolDetails?.address &&
                                        <li className="flex items-start space-x-4 flex items-center">
                                            <span className="text-primary text-2xl"><FaAddressCard /></span>
                                            <div>
                                                <strong>{t("address")}:</strong><br />
                                                {schoolDetails?.address}
                                            </div>
                                        </li>
                                    }
                                    {schoolDetails?.contact_number &&
                                        <li className="flex items-start space-x-4 flex items-center">
                                            <span className="text-primary text-2xl"><FaPhoneAlt /></span>
                                            <div>
                                                <strong>{t("phone")}:</strong><br />
                                                {schoolDetails?.contact_number}
                                            </div>
                                        </li>
                                    }
                                    {schoolDetails?.email &&
                                        <li className="flex items-start space-x-4 flex items-center">
                                            <span className="text-primary text-2xl"><MdEmail /></span>
                                            <div>
                                                <strong>{t("email")}:</strong><br />
                                                {schoolDetails?.email}
                                            </div>
                                        </li>
                                    }
                                    {schoolDetails?.working_hours &&
                                        <li className="flex items-start space-x-4 flex items-center">
                                            <span className="text-primary text-2xl"><MdWatchLater /></span>
                                            <div>
                                                <strong>{t("working_hours")}:</strong><br />
                                                {schoolDetails?.working_hours}
                                            </div>
                                        </li>
                                    }
                                </ul>
                                <p className="mt-10 text-primary italic">
                                    {t(I18N_KEYS.CONTACTUSINFO)}
                                </p>
                            </motion.div>}

                        {/* Contact Form */}
                        <motion.div
                            className="bg-accent rounded-3xl shadow-2xl p-10"
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            {formDefination &&
                                <FormRenderer
                                    formSchema={formDefination}
                                    colors={{ 'primary': getPrimaryColor(), 'secondary': getSecondaryColor() }}
                                    services={services}
                                    onSubmit={handleSubmit}
                                    onSuccess={() => toast.success("Your message has been received!We’ll get back to you shortly.")}
                                    hideTitle={true}
                                />
                            }
                        </motion.div>
                    </div>
                </section>

                {/* Map Section */}
                {schoolDetails?.location &&
                    <section className="w-full h-96 mb-10">
                        <iframe
                            src={schoolDetails?.location}
                            title="School Location Map"
                            className="w-full h-full border-0"
                            width="600"
                            height="450"
                            loading="lazy"
                            referrerpolicy="no-referrer-when-downgrade"></iframe>
                    </section>
                }
            </div>
        </>
    );
}

export default Contact